// Typed client for the HIPAA audit-trail read API
// (core.rpc_admin_get_audit_trail + core.fn_audit_chain_verify).
//
// Powers the Audit tabs in apps/admin (per-org / per-store / per-user)
// and the global Audit Explorer page.

import { supabase } from '../client';
import {
  clampPageSize,
  type KeysetCursor,
  type ListResult,
} from '../pagination';

export type AuditSource = 'audit_log' | 'session_audit' | 'system_event_log';
export type AuditSeverity = 'info' | 'warn' | 'error' | 'security';
export type AuditSubjectKind = 'platform' | 'org' | 'store' | 'user' | 'row';

export interface AuditTrailRow {
  source: AuditSource;
  source_id: number;
  at: string;
  /** session_audit: kind enum. system_event_log: dot-notated event kind. audit_log: null. */
  kind: string | null;
  /** audit_log: INSERT/UPDATE/DELETE/RESTORE/LOGIN/LOGOUT/SYSTEM/SECURITY. */
  action: string | null;
  severity: AuditSeverity;
  actor_id: string | null;
  actor_email: string | null;
  actor_kind: string | null;
  org_id: string | null;
  store_id: string | null;
  subject_schema: string | null;
  subject_table: string | null;
  subject_row_id: string | null;
  ip: string | null;
  user_agent: string | null;
  request_id: string | null;
  session_id: string | null;
  route: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  payload: Record<string, unknown> | null;
  /** Per-org hash chain sequence (audit_log + session_audit only). */
  seq: number | null;
  prev_row_hash: string | null;
  row_hash: string | null;
}

export interface GetAuditTrailInput {
  subjectKind: AuditSubjectKind;
  subjectId?: string | null;
  kinds?: string[] | null;
  severities?: AuditSeverity[] | null;
  actorId?: string | null;
  from?: string | null;
  to?: string | null;
  /** Keyset cursor. `sortValue` is the prior page's last `at` (ISO),
   *  `id` is the prior page's last `source_id` as a string. */
  cursor?: KeysetCursor | null;
  pageSize?: number;
}

/**
 * Paginated keyset audit trail view, returning the standard
 * `ListResult<AuditTrailRow>` shape used by every other list service.
 *
 * The underlying RPC `core.rpc_admin_get_audit_trail` already keyset-
 * paginates internally on `(at desc, source_id desc)`; this wrapper
 * adapts its raw row+cursor protocol to the project's shared
 * `ListResult` contract so the same `useInfiniteList` hook can drive it.
 *
 * Total counts are not supported here -- the RPC unions audit_log,
 * session_audit, and system_event_log, and an exact COUNT(*) across all
 * three is prohibitively expensive for production-sized tenants. The
 * UI shows "X events loaded" instead of "X of N".
 */
export async function getAuditTrail(
  input: GetAuditTrailInput,
): Promise<ListResult<AuditTrailRow>> {
  const pageSize = clampPageSize(input.pageSize);
  const cursorAtRaw = input.cursor?.sortValue ?? null;
  const cursorIdRaw = input.cursor?.id != null ? Number(input.cursor.id) : null;

  const { data, error } = await supabase().schema('core').rpc('rpc_admin_get_audit_trail', {
    p_subject_kind: input.subjectKind,
    p_subject_id: input.subjectId ?? undefined,
    p_kinds: input.kinds ?? undefined,
    p_severities: input.severities ?? undefined,
    p_actor_id: input.actorId ?? undefined,
    p_from: input.from ?? undefined,
    p_to: input.to ?? undefined,
    p_cursor_at: cursorAtRaw ?? undefined,
    p_cursor_id: cursorIdRaw ?? undefined,
    p_limit: pageSize,
  });
  if (error) throw error;
  const rows = ((data ?? []) as unknown as AuditTrailRow[]) ?? [];
  const last = rows[rows.length - 1];
  const nextCursor =
    rows.length >= pageSize && last ? { sortValue: last.at, id: String(last.source_id) } : null;
  return { rows, nextCursor, total: null, totalIsExact: false };
}

export interface AuditChainStatus {
  ok: boolean;
  last_verified_seq: number | null;
  last_verified_at: string | null;
  first_break_seq: number | null;
  first_break_at: string | null;
}

export async function verifyAuditChain(orgId: string | null): Promise<AuditChainStatus> {
  // p_org_id is required by signature but nullable at runtime (NULL =
  // platform chain). Widen via cast so the TS generated type doesn't
  // complain about undefined.
  const { data, error } = await supabase().schema('core').rpc('fn_audit_chain_verify', {
    p_org_id: (orgId ?? undefined) as unknown as string,
  });
  if (error) throw error;
  const rows = (data ?? []) as unknown as AuditChainStatus[];
  return rows[0] ?? {
    ok: true,
    last_verified_seq: null,
    last_verified_at: null,
    first_break_seq: null,
    first_break_at: null,
  };
}

export interface RestoreRecordInput {
  schema: string;
  table: string;
  rowId: string;
}

export async function restoreRecord(input: RestoreRecordInput): Promise<void> {
  const { error } = await supabase().schema('core').rpc('rpc_admin_restore_record', {
    p_schema: input.schema,
    p_table: input.table,
    p_row_id: input.rowId,
  });
  if (error) throw error;
}
